export class HybridMarker {
  /** @type { ?google.maps.OverlayView } */
  static #manualDraggingOverlay = null;

  static get LegacyMarker() {
    return globalThis.google.maps.Marker;
  }

  static get AdvancedMarker() {
    return globalThis.google.maps.marker.AdvancedMarkerElement;
  }

  static LegacyMarkerProperties = new Set([
    'anchorPoint',
    'animation',
    'clickable',
    'crossOnDrag',
    'cursor',
    'draggable',
    'icon',
    'label',
    'map',
    'opacity',
    'optimized',
    'position',
    'shape',
    'title',
    'visible',
    'zIndex'
  ]);
  static AdvancedMarkerProperties = new Set(['collisionBehavior', 'content', 'gmpDraggable', 'map', 'position', 'title', 'zIndex']);
  static get MarkerProperties() {
    return new Set([...HybridMarker.LegacyMarkerProperties, ...HybridMarker.AdvancedMarkerProperties]);
  }

  /**
   * Internal marker instance.
   * @type {HybridMarker.LegacyMarker|HybridMarker.AdvancedMarker}
   */
  #marker;
  #options;
  #eventListeners = [];
  #customDraggable = false;
  #customDraggingTarget;

  get marker() {
    return this.#marker;
  }

  get customDraggable() {
    return this.#customDraggable;
  }

  set customDraggable(value) {
    this.#customDraggable = value;
    this.#setupCustomDragging();
  }

  constructor(options) {
    Array.from(HybridMarker.MarkerProperties).forEach((prop) => {
      Object.defineProperty(this, prop, {
        get: () => this.#getOption(prop),
        set: (value) => this.#setOption(prop, value)
      });
    });

    this.#options = options;
    const markerClass = this.#inferMarkerType(options);
    this.#rebuildMarkerInstance(markerClass);

    this.customDraggable = options.customDraggable ?? false;
  }

  addEventListener(eventType, callback) {
    this.#eventListeners.push({ eventType, callback });
    globalThis.google.maps.event.addListener(this.#marker, eventType, callback);
  }

  /**
   * @param {typeof HybridMarker.LegacyMarker|typeof HybridMarker.AdvancedMarker} markerClass
   * @param {Object} options - A list of options to be used to create the marker.
   * @returns {HybridMarker.LegacyMarker|HybridMarker.AdvancedMarker}
   */
  #getMarkerInstance(markerClass, options) {
    const markerOptions = this.#getMarkerOptions(markerClass, options);
    const marker = new markerClass(markerOptions);
    return marker;
  }

  #rebuildMarkerInstance(markerClass) {
    this.#marker = this.#getMarkerInstance(markerClass, this.#options);
    this.#eventListeners.forEach(({ eventType, callback }) => globalThis.google.maps.event.addListener(this.#marker, eventType, callback));
    this.#setupCustomDragging();
  }

  #setupCustomDragging() {
    if (!this.#customDraggable) return;

    if (this.#marker instanceof HybridMarker.LegacyMarker) {
      if (this.#customDraggingTarget === this.#marker) return;
      this.addEventListener('mousedown', (e) => {
        if (!this.#customDraggable) return;
        e.domEvent.stopPropagation(); // Prevents map from beginning to pan.
        this.#beginCustomDragging();
      });
      this.#customDraggingTarget = this.#marker;
    }

    if (this.#marker instanceof HybridMarker.AdvancedMarker) {
      const content = this.#getOption('content');
      if (this.#customDraggingTarget === content) return;
      content.addEventListener('pointerdown', (e) => {
        if (!this.#customDraggable) return;
        e.stopPropagation(); // Prevents Advanced Map Marker's dragging code from running.
        this.#beginCustomDragging();
      });
      this.#customDraggingTarget = content;
    }
  }

  #beginCustomDragging() {
    const [dragstart, drag, dragend] = ['dragstart', 'drag', 'dragend'].map((type) => (e) => {
      this.#eventListeners.forEach(({ eventType, callback }) => {
        if (eventType === type) callback(e);
      });
    });

    const map = this.#getOption('map');
    const overlay = (HybridMarker.#manualDraggingOverlay ??= new globalThis.google.maps.OverlayView());
    if (overlay.getMap() == null) overlay.setMap(map);

    const projection = map.getProjection();
    const mapDiv = map.getDiv();

    let firstMove = true;
    let lastEvent;
    let pointerOffset;
    const pointerMoveListener = (e) => {
      const rect = map.getDiv().getBoundingClientRect();
      const pointerCoords = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const pointerLatLng = overlay.getProjection().fromDivPixelToLatLng(pointerCoords);

      if (!this.customDraggable) {
        return endDrag();
      }

      if (firstMove) {
        const position = this.#getOption('position');
        const markerPoint = projection.fromLatLngToPoint(position);
        const pointerPoint = projection.fromLatLngToPoint(pointerLatLng);
        pointerOffset = { x: markerPoint.x - pointerPoint.x, y: markerPoint.y - pointerPoint.y };
      }
      const pointerPoint = projection.fromLatLngToPoint(pointerLatLng);
      e.latLng = projection.fromPointToLatLng({ x: pointerPoint.x + pointerOffset.x, y: pointerPoint.y + pointerOffset.y });
      if (firstMove) {
        firstMove = false;
        dragstart?.(e);
      }
      drag?.(e);
      lastEvent = e;
    };
    mapDiv.addEventListener('pointermove', pointerMoveListener);

    const pointerUpListener = () => endDrag();
    window.addEventListener('pointerup', pointerUpListener, { once: true });

    // Function to end dragging and cleanup event listeners.
    const endDrag = () => {
      mapDiv.removeEventListener('pointermove', pointerMoveListener);
      window.removeEventListener('pointerup', pointerUpListener);

      if (firstMove) return;
      dragend?.(lastEvent);
    };
  }

  /**
   * Accepts a marker class and options and returns the options that are valid for the marker class.
   * May also transform options to fit the marker class.
   * @param {typeof HybridMarker.LegacyMarker|typeof HybridMarker.AdvancedMarker} markerClass
   * @param {Object} options - A list of options to be used to create the marker.
   * @returns {Object} The options that are valid for the given marker class.
   */
  #getMarkerOptions(markerClass, options) {
    switch (markerClass) {
      case HybridMarker.LegacyMarker: {
        return this.#getLegacyMarkerOptions(options);
      }
      case HybridMarker.AdvancedMarker: {
        return this.#getAdvancedMarkerOptions(options);
      }
    }
  }

  #getLegacyMarkerOptions(options) {
    const defaultLegacyMarkerOptions = { optimized: true };
    const legacyMarkerOptions = { ...defaultLegacyMarkerOptions };
    for (const key in options) {
      const { property, value } = this.#getLegacyMarkerPropertyDetails(key, options[key]) ?? {};
      if (value != null) legacyMarkerOptions[property] = value;
    }
    return legacyMarkerOptions;
  }

  #getLegacyMarkerPropertyDetails(property, value) {
    switch (property) {
      case 'gmpClickable': {
        property = 'clickable';
        break;
      }
      case 'gmpDraggable': {
        property = 'draggable';
        break;
      }
    }
    if (HybridMarker.LegacyMarkerProperties.has(property)) return { property, value };
  }

  #getAdvancedMarkerOptions(options) {
    const defaultAdvancedMarkerOptions = {};
    const advancedMarkerOptions = { ...defaultAdvancedMarkerOptions };
    for (const key in options) {
      const { property, value } = this.#getAdvancedMarkerPropertyDetails(key, options[key]) ?? {};
      if (value != null) advancedMarkerOptions[property] = value;
    }
    return advancedMarkerOptions;
  }
  #getAdvancedMarkerPropertyDetails(property, value) {
    switch (property) {
      case 'clickable': {
        property = 'gmpClickable';
        break;
      }
      case 'draggable': {
        property = 'gmpDraggable';
        break;
      }
      case 'icon': {
        property = 'content';
        value = this.#iconToContent(value);
        break;
      }
    }
    if (HybridMarker.AdvancedMarkerProperties.has(property)) return { property, value };
  }

  #iconToContent(icon) {
    const url = icon?.url ?? icon;
    if (typeof url !== 'string') return;
    const img = document.createElement('img');
    img.src = url;
    Object.entries({
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
      userSelect: 'none'
    }).forEach(([key, value]) => (img.style[key] = value));
    return img;
  }

  /**
   *
   * @param {Object} options A list of options to be used to create the marker.
   * @returns {typeof HybridMarker.LegacyMarker|typeof HybridMarker.AdvancedMarker} The class of the marker to be used.
   */
  #inferMarkerType(options) {
    const mayUseAdvancedMarker = options.useAdvancedMarkerElement !== false;
    const mustUseAdvancedMarker = options.useAdvancedMarkerElement === true;

    const markerIsDraggable = options.draggable === true || options.gmpDraggable === true;
    const markerMustNotBeOptimized = options.optimized === false;
    const advancedMarkerOptionsPresent = Object.keys(options).some(
      (x) => HybridMarker.AdvancedMarkerProperties.has(x) && !HybridMarker.LegacyMarkerProperties.has(x)
    );
    const shouldUseAdvancedMarker = markerIsDraggable || markerMustNotBeOptimized || advancedMarkerOptionsPresent;

    const willUseAdvancedMarker = mustUseAdvancedMarker || (mayUseAdvancedMarker && shouldUseAdvancedMarker);
    return willUseAdvancedMarker ? HybridMarker.AdvancedMarker : HybridMarker.LegacyMarker;
  }

  #getOption(prop) {
    return this.#getMarkerProperty(prop);
  }

  #setOption(prop, value) {
    this.#options[prop] = value;

    const markerClass = this.#inferMarkerType(this.#options);
    if (!(this.#marker instanceof markerClass)) {
      this.#setMarkerProperty('map', null);
      this.#rebuildMarkerInstance(markerClass);
    } else {
      this.#setMarkerProperty(prop, value);
    }
  }

  #getMarkerProperty(prop) {
    if (this.#marker instanceof HybridMarker.LegacyMarker) {
      const { property } = this.#getLegacyMarkerPropertyDetails(prop) ?? {};
      if (!property) return;
      switch (property) {
        case 'animation':
          return this.#marker.getAnimation();
        case 'clickable':
          return this.#marker.getClickable();
        case 'cursor':
          return this.#marker.getCursor();
        case 'draggable':
          return this.#marker.getDraggable();
        case 'icon':
          return this.#marker.getIcon();
        case 'label':
          return this.#marker.getLabel();
        case 'map':
          return this.#marker.getMap();
        case 'opacity':
          return this.#marker.getOpacity();
        case 'position':
          return this.#marker.getPosition();
        case 'shape':
          return this.#marker.getShape();
        case 'title':
          return this.#marker.getTitle();
        case 'visible':
          return this.#marker.getVisible();
        case 'zIndex':
          return this.#marker.getZIndex();
        default:
          return;
      }
    }

    if (this.#marker instanceof HybridMarker.AdvancedMarker) {
      const { property } = this.#getAdvancedMarkerPropertyDetails(prop) ?? {};
      if (!property) return;
      return this.#marker[property];
    }
  }

  #setMarkerProperty(prop, val) {
    if (this.#marker instanceof HybridMarker.LegacyMarker) {
      const { property, value } = this.#getLegacyMarkerPropertyDetails(prop, val) ?? {};
      if (!property) return;
      if (property == 'label') console.log(this, prop, value, property, value);
      switch (property) {
        case 'animation':
          return this.#marker.setAnimation(value);
        case 'clickable':
          return this.#marker.setClickable(value);
        case 'cursor':
          return this.#marker.setCursor(value);
        case 'draggable':
          return this.#marker.setDraggable(value);
        case 'icon':
          return this.#marker.setIcon(value);
        case 'label':
          return this.#marker.setLabel(value);
        case 'map':
          return this.#marker.setMap(value);
        case 'opacity':
          return this.#marker.setOpacity(value);
        case 'position':
          return this.#marker.setPosition(value);
        case 'shape':
          return this.#marker.setShape(value);
        case 'title':
          return this.#marker.setTitle(value);
        case 'visible':
          return this.#marker.setVisible(value);
        case 'zIndex':
          return this.#marker.setZIndex(value);
        default:
          return;
      }
    }

    if (this.#marker instanceof HybridMarker.AdvancedMarker) {
      const { property, value } = this.#getAdvancedMarkerPropertyDetails(prop, val) ?? {};
      if (!property) return;
      this.#marker[property] = value;
      if (property === 'content') this.#setupCustomDragging();
      return value;
    }
  }
}
